import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  RefreshControl,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { getSystemAnalytics, SystemAnalytics } from "../../services/parseService";

interface StatProps {
  label: string;
  value: string | number;
  hint?: string;
  color?: string;
  bg?: string;
}

const StatCard: React.FC<StatProps> = ({ label, value, hint, color = "#111827", bg = "#FFFFFF" }) => (
  <View style={[styles.statCard, { backgroundColor: bg }]}>
    <Text style={[styles.statValue, { color }]}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
    {!!hint && <Text style={styles.statHint}>{hint}</Text>}
  </View>
);

const CoverageBar: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
  <View style={styles.barTrack}>
    <View style={[styles.barFill, { width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color }]} />
  </View>
);

const formatRelative = (iso: string | null): string => {
  if (!iso) return "never";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "unknown";
  const diffMs = Date.now() - then.getTime();
  const h = Math.floor(diffMs / 3_600_000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
};

const AnalyticsScreen: React.FC = () => {
  const [data, setData] = useState<SystemAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setError(null);
      const res = await getSystemAnalytics();
      setData(res);
    } catch (err) {
      setError("Failed to load analytics.");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onRefresh = () => {
    setIsRefreshing(true);
    load();
  };

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563EB" />
        <Text style={styles.loadingText}>Loading analytics…</Text>
      </View>
    );
  }

  if (error || !data) {
    return (
      <View style={styles.center}>
        <Ionicons name="warning-outline" size={32} color="#DC2626" />
        <Text style={styles.loadingText}>{error || "No data."}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={load}>
          <Text style={styles.retryText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const { content, crawler, queues } = data;

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} tintColor="#2563EB" />}
    >
      <View style={styles.headerRow}>
        <View>
          <Text style={styles.title}>System Overview</Text>
          <Text style={styles.subtitle}>Last crawl: {formatRelative(crawler.lastCrawlAt)}</Text>
        </View>
        <TouchableOpacity style={styles.refreshButton} onPress={onRefresh} disabled={isRefreshing}>
          <Ionicons name="refresh" size={20} color={isRefreshing ? "#9CA3AF" : "#2563EB"} />
        </TouchableOpacity>
      </View>

      {/* Crawler */}
      <Text style={styles.sectionTitle}>Crawler</Text>
      <View style={styles.grid}>
        <StatCard label="Pages screened (nano)" value={crawler.nanoCalls} hint="reached relevance gate" />
        <StatCard label="Pages extracted (mini)" value={crawler.miniCalls} hint="full AI extraction" />
        <StatCard label="Rejected by relevance" value={crawler.nanoRejects} color="#D97706" bg="#FFFBEB" />
        <StatCard label="Fetch/extract errors" value={crawler.errors} color={crawler.errors > 0 ? "#DC2626" : "#111827"} bg={crawler.errors > 0 ? "#FEF2F2" : "#FFFFFF"} />
        <StatCard label="URLs tracked" value={crawler.urlsTracked} hint="in CrawlLog ledger" />
        <StatCard label="Queued last 7 days" value={crawler.queuedLast7Days} color="#2563EB" bg="#EEF2FF" />
      </View>
      <Text style={styles.note}>
        LLM call volumes are derived from the crawl ledger — a ballpark to gauge activity, not exact provider billing.
      </Text>

      {/* Review queues */}
      <Text style={styles.sectionTitle}>Review Queues</Text>
      <View style={styles.grid}>
        <StatCard label="Crawled · pending" value={queues.crawledPending} color="#2563EB" bg="#EEF2FF" />
        <StatCard label="Crawled · approved" value={queues.crawledApproved} color="#16A34A" bg="#F0FDF4" />
        <StatCard label="Crawled · rejected" value={queues.crawledRejected} color="#6B7280" />
        <StatCard label="Approval rate" value={`${queues.approvalRatePct}%`} hint="approved of reviewed" />
        <StatCard label="Issues pending" value={queues.issuesPending} color={queues.issuesPending > 0 ? "#D97706" : "#111827"} bg={queues.issuesPending > 0 ? "#FFFBEB" : "#FFFFFF"} />
        <StatCard label="Suggestions pending" value={queues.suggestionsPending} />
      </View>

      {/* Content & search */}
      <Text style={styles.sectionTitle}>Content & Search</Text>
      <View style={styles.grid}>
        <StatCard label="Total opportunities" value={content.totalOpportunities} />
        <StatCard label="Org-claimed" value={content.claimedOpportunities} hint="managed by orgs" />
      </View>
      <View style={styles.coverageCard}>
        <View style={styles.coverageHeader}>
          <Text style={styles.coverageLabel}>Semantic search coverage</Text>
          <Text style={styles.coveragePct}>{content.embeddingCoveragePct}%</Text>
        </View>
        <CoverageBar pct={content.embeddingCoveragePct} color={content.embeddingCoveragePct >= 90 ? "#16A34A" : content.embeddingCoveragePct >= 60 ? "#D97706" : "#DC2626"} />
        <Text style={styles.coverageHint}>
          {content.embeddings} of {content.totalOpportunities} opportunities have embeddings. Low coverage degrades AI search — regenerate from the Dashboard.
        </Text>
      </View>

      <View style={{ height: 32 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },
  center: { flex: 1, justifyContent: "center", alignItems: "center", padding: 24 },
  loadingText: { marginTop: 12, color: "#6B7280", fontSize: 15, fontFamily: "Inter-Medium" },
  retryButton: { marginTop: 16, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, backgroundColor: "#2563EB" },
  retryText: { color: "#FFFFFF", fontFamily: "Inter-Medium" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  title: { fontSize: 22, fontWeight: "700", color: "#111827", fontFamily: "Inter-Bold" },
  subtitle: { fontSize: 13, color: "#6B7280", marginTop: 2, fontFamily: "Inter-Regular" },
  refreshButton: { width: 40, height: 40, borderRadius: 8, backgroundColor: "#F9FAFB", alignItems: "center", justifyContent: "center", borderWidth: 1, borderColor: "#E5E7EB" },
  sectionTitle: { fontSize: 16, fontWeight: "600", color: "#111827", marginTop: 20, marginBottom: 12, fontFamily: "Inter-SemiBold" },
  grid: { flexDirection: "row", flexWrap: "wrap", marginHorizontal: -6 },
  statCard: { width: "33.33%", minWidth: 140, flexGrow: 1, marginHorizontal: 6, marginBottom: 12, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: "#E5E7EB" },
  statValue: { fontSize: 26, fontWeight: "700", fontFamily: "Inter-Bold" },
  statLabel: { fontSize: 13, color: "#374151", marginTop: 4, fontFamily: "Inter-Medium" },
  statHint: { fontSize: 11, color: "#9CA3AF", marginTop: 2, fontFamily: "Inter-Regular" },
  note: { fontSize: 12, color: "#9CA3AF", fontStyle: "italic", marginTop: 2, fontFamily: "Inter-Regular" },
  coverageCard: { backgroundColor: "#FFFFFF", borderRadius: 12, borderWidth: 1, borderColor: "#E5E7EB", padding: 16, marginTop: 4 },
  coverageHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  coverageLabel: { fontSize: 14, color: "#374151", fontFamily: "Inter-Medium" },
  coveragePct: { fontSize: 16, fontWeight: "700", color: "#111827", fontFamily: "Inter-Bold" },
  barTrack: { height: 10, borderRadius: 5, backgroundColor: "#F1F5F9", overflow: "hidden" },
  barFill: { height: 10, borderRadius: 5 },
  coverageHint: { fontSize: 12, color: "#6B7280", marginTop: 10, lineHeight: 18, fontFamily: "Inter-Regular" },
});

export default AnalyticsScreen;
